import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import { GenericBoundedApiService, ResponseCode } from '@lsq-frontend/angular-shared/api-service';
import { GenericApiServiceFactory } from '@lsq-frontend/angular-shared/api-service';
import { AuthenticationService } from '../user/authentication.service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { UiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { first } from 'rxjs/operators';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';

@Injectable({ providedIn: 'root' })
export class SessionService {
    private _logoutService: GenericBoundedApiService;
    private _timeoutService: GenericBoundedApiService;
    private _pageClosedService: GenericBoundedApiService;

    constructor(
        public authenticationService: AuthenticationService,
        public genericApiServiceFactory: GenericApiServiceFactory,
        public router: Router,
        public userService: UserService,
        private _errorToastBlacklistService: ErrorToastBlacklistService
    ) {
        SingletonServiceGuard.register('SessionService');
        this._logoutService = this.genericApiServiceFactory.createBounded(
            `/${ApiRoutes.Api}/${ApiRoutes.Logout}`
        );
        this._timeoutService = this.genericApiServiceFactory.createBounded(
            `/${ApiRoutes.Api}/${ApiRoutes.Timeout}`
        );
        this._pageClosedService = this.genericApiServiceFactory.createBounded(
            `/${ApiRoutes.Api}/${ApiRoutes.PageClosed}`
        );
        // We want this service to fail silently, it was throwing errors in FireFox when refreshing.
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${ApiRoutes.Api}/${ApiRoutes.PageClosed}`,
            ResponseCode.None
        );
    }

    public async logout(redirectUrl = UiRoutes.Login): Promise<void> {
        await this._logoutService.executeOnce();
        this._clearSession();
        await this.router.navigateByUrl(redirectUrl);
    }

    public async timeout(redirectUrl = UiRoutes.Login): Promise<void> {
        await this._timeoutService.executeOnce();
        this._clearSession();
        await this.router.navigateByUrl(redirectUrl);
    }

    public async pageClosed(): Promise<void> {
        const isAuthed = await this.userService.user$.pipe(first()).toPromise();
        if (isAuthed) {
            // This endpoint requires authentication, so make sure we have a user first.
            await this._pageClosedService.executeOnce();
        }
    }

    private _clearSession(): void {
        this.authenticationService.clearSession();
        this.userService.clearSession();
    }
}
